import { UserCircleIcon, PencilSquareIcon, TrashIcon, ExclamationTriangleIcon, UserPlusIcon, LockClosedIcon, LockOpenIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "contexts/LanguageContext";
import { useState, FormEvent, useMemo } from "react";
import { User } from '../../types/user';
import { Tooltip } from "components/common/Tooltip";
import Modal from "components/common/Modal";
import { useUsers } from "hooks/useUsers";
import ConfirmDeleteModal from "components/common/ConfirmDeleteModal";
import Pagination from "components/common/Pagination";
import LoadingScreen from '../../components/common/LoadingScreen';
import CommonPageHeader from '../../components/common/CommonPageHeader';

const Users = () => {
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { t } = useLanguage();
  const { users, loading, createUser, updateUser, deleteUser, unlockUser } = useUsers();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Helper function to safely format dates
  const formatDate = (date: any, includeTime = false) => {
    if (!date) return '';
    
    try {
      // Convert to JavaScript Date if it's not already
      const jsDate = date instanceof Date ? date : new Date(date);
      
      // Check if date is valid
      if (isNaN(jsDate.getTime())) {
        return t('systemAdmin.users.never');
      }
      
      // Format based on whether time should be included - using Hungarian format
      return includeTime 
        ? jsDate.toLocaleString('hu-HU', {
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          })
        : jsDate.toLocaleDateString('hu-HU', {
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit'
          });
    } catch (error) {
      console.error('Error formatting date:', error);
      return t('systemAdmin.users.never');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-500/20 text-purple-600';
      case 'admin':
        return 'bg-blue-500/20 text-blue-600';
      default:
        return 'bg-gray-500/20 text-gray-600';
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = users.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(users.length / itemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleUnlockUser = async (user: User) => {
    try {
      await unlockUser(user);
    } catch (error) {
      console.error('Error unlocking user:', error);
    }
  };

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleting(false);
    setSelectedUser(null);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleSaveUser = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedUser) return;

    const form = e.currentTarget;
    const formData = new FormData(form);

    const updatedUser = {
      displayName: formData.get('displayName') as string,
      email: formData.get('email') as string,
      role: formData.get('role') as 'super_admin' | 'admin' | 'user',
    };

    try {
      setSaving(true);

      // The updateUser function in the hook now updates the local state
      await updateUser(selectedUser.id, updatedUser);

      closeEditModal();
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const newUser = {
      displayName: formData.get('displayName') as string,
      email: formData.get('email') as string,
      role: formData.get('role') as 'super_admin' | 'admin' | 'user',
      photoURL: '',
      organizationId: '',
      id: '', // This will be generated by Firebase
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      setCreating(true);

      await createUser(newUser);

      closeCreateModal();
      form.reset();
    } catch (error) {
      console.error('Error creating user:', error);
    } finally {
      setCreating(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setDeleting(true);
      await deleteUser(selectedUser.id);
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      closeDeleteModal();
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6 pb-4 sm:pb-4">
      <CommonPageHeader
        title={t('systemAdmin.users.title')}
        subtitle="Felhasználók kezelése és jogosultságok"
        icon={UserCircleIcon}
        showLastUpdate={false}
      >
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
        >
          <UserPlusIcon className="w-5 h-5 mr-2" />
          {t('systemAdmin.users.addUser')}
        </button>
      </CommonPageHeader>

      {/* Desktop view - table */}
      <div className="bg-white rounded-lg overflow-hidden hidden md:block shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">{t('systemAdmin.users.fields.user')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">{t('systemAdmin.users.fields.email')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">{t('systemAdmin.users.fields.role')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">{t('systemAdmin.users.fields.created')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">{t('systemAdmin.users.fields.lastLogin')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">{t('systemAdmin.users.fields.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    {t('common.noResults')}
                  </td>
                </tr>
              ) : (
                currentUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {user.photoURL ? (
                          <img
                            src={user.photoURL}
                            alt=""
                            className="h-8 w-8 rounded-full"
                          />
                        ) : (
                          <UserCircleIcon className="h-8 w-8 text-gray-500" />
                        )}
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-800 flex items-center gap-2">
                            {user.displayName || t('systemAdmin.users.noName')}
                            {user.locked && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                                <LockClosedIcon className="h-3 w-3" />
                                {t('systemAdmin.users.locked') || 'Zárolt'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(user.role)}`}>
                        {t(`systemAdmin.users.roles.${user.role}`)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatDate(user.lastLoginAt, true)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-3">
                        {user.locked && (
                          <Tooltip content={t('systemAdmin.users.unlock') || 'Feloldás'}>
                            <button
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleUnlockUser(user)}
                            >
                              <LockOpenIcon className="h-5 w-5" />
                            </button>
                          </Tooltip>
                        )}
                        <Tooltip content={t('common.edit')}>
                          <button
                            className="text-primary-600 hover:text-primary-700"
                            onClick={() => handleEditUser(user)}
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                        </Tooltip>
                        <Tooltip content={t('common.delete')}>
                          <button
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteUser(user)}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile view - cards */}
      <div className="md:hidden space-y-4 mb-4">
        {currentUsers.length === 0 ? (
          <div className="bg-white p-4 rounded-lg text-center text-gray-500 border border-gray-200 shadow-sm">
            {t('common.noResults')}
          </div>
        ) : (
          currentUsers.map((user) => (
            <div key={user.id} className="bg-white rounded-lg p-4 space-y-3 shadow-sm border border-gray-200">
              <div className="flex justify-between items-start">
                <div className="flex items-center">
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt=""
                      className="h-10 w-10 rounded-full"
                    />
                  ) : (
                    <UserCircleIcon className="h-10 w-10 text-gray-500" />
                  )}
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-800 flex items-center gap-2">
                      {user.displayName || t('systemAdmin.users.noName')}
                      {user.locked && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                          <LockClosedIcon className="h-3 w-3" />
                          {t('systemAdmin.users.locked') || 'Zárolt'}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 mt-1">{user.email}</div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {user.locked && (
                    <button
                      className="text-green-600 hover:text-green-700"
                      onClick={() => handleUnlockUser(user)}
                    >
                      <LockOpenIcon className="h-5 w-5" />
                    </button>
                  )}
                  <button
                    className="text-primary-600 hover:text-primary-700"
                    onClick={() => handleEditUser(user)}
                  >
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>
                  <button
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDeleteUser(user)}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('systemAdmin.users.fields.role')}:</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(user.role)}`}>
                    {t(`systemAdmin.users.roles.${user.role}`)}
                  </span>
                </div>

                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">{t('systemAdmin.users.fields.created')}:</span>
                  <span className="text-gray-700">
                    {formatDate(user.createdAt)}
                  </span>
                </div>

                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">{t('systemAdmin.users.fields.lastLogin')}:</span>
                  <span className="text-gray-700">
                    {formatDate(user.lastLoginAt, true)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {users.length > 0 && (
        <div className="relative z-10">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={users.length}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        title={t('systemAdmin.users.editUser')}
        size="lg"
      >
        {selectedUser && (
          <form onSubmit={handleSaveUser} className="p-6 space-y-6">
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
              <div className="flex-shrink-0">
                {selectedUser.photoURL ? (
                  <img
                    src={selectedUser.photoURL}
                    alt=""
                    className="h-24 w-24 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200">
                    <UserCircleIcon className="h-16 w-16 text-gray-500" />
                  </div>
                )}
              </div>
              <div className="space-y-4 flex-grow">
                <div>
                  <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                    {t('systemAdmin.users.fields.name')}
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    name="displayName"
                    defaultValue={selectedUser.displayName}
                    className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-700 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    {t('systemAdmin.users.fields.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    defaultValue={selectedUser.email}
                    className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-700 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                {t('systemAdmin.users.fields.role')}
              </label>
              <select
                id="role"
                name="role"
                defaultValue={selectedUser.role}
                className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-700 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="super_admin">{t('systemAdmin.users.roles.super_admin')}</option>
                <option value="admin">{t('systemAdmin.users.roles.admin')}</option>
                <option value="user">{t('systemAdmin.users.roles.user')}</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeEditModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-primary-500/50 disabled:cursor-not-allowed"
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteUser}
        title={t('systemAdmin.users.deleteUser')}
        deleteMessage={selectedUser ?
          `${t('systemAdmin.messages.user.deleteQuestion')} "${selectedUser.displayName || t('systemAdmin.users.noName')}" (${selectedUser.email})` :
          t('systemAdmin.messages.user.deleteQuestion')}
        deleteWarning={t('systemAdmin.messages.user.deleteWarning')}
      />

      {/* Create User Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title={t('systemAdmin.users.addUser')}
        size="lg"
      >
        <form onSubmit={handleCreateUser} className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                {t('systemAdmin.users.fields.name')}
              </label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                required
                placeholder={t('systemAdmin.users.placeholders.name')}
                className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-700 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('systemAdmin.users.fields.email')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                placeholder={t('systemAdmin.users.placeholders.email')}
                className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-700 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                {t('systemAdmin.users.fields.role')}
              </label>
              <select
                id="role"
                name="role"
                required
                defaultValue="user"
                className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-700 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="super_admin">{t('systemAdmin.users.roles.super_admin')}</option>
                <option value="admin">{t('systemAdmin.users.roles.admin')}</option>
                <option value="user">{t('systemAdmin.users.roles.user')}</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={closeCreateModal}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-primary-500/50 disabled:cursor-not-allowed"
            >
              {creating ? t('common.creating') : t('common.create')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Users;


